// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Playing sitting down (M5-26, XR-007).
//
// Two separate things that are easy to conflate:
//
// - **Detecting that a player is seated**, so comfort mode can be *offered*. 06 §6: "offered
//   automatically if the app detects the player has been seated and stationary for the first 30
//   seconds." Offered — never imposed. A standing player who happens to be still for half a
//   minute while reading the machine is not a seated player, and switching their controls out
//   from under them would be the worst possible first impression.
// - **Repositioning the machine** once they take the offer, so nothing needs reaching for.
//
// **Seated is a height, and stationary is a variance.** Both are needed and neither is enough on
// its own: a short standing player and a tall seated one overlap, and a standing player waiting
// for a ball is stationary. What separates them is that a seated head stays inside a small volume
// *and* sits low, for a long time, without ever standing up in between.
//
// The offer is made once. A player who declines is not asked again in the same session, because
// a suggestion that keeps coming back is not a suggestion.

import { yawFacing, type Placement } from './placement'
import { standOffFor } from './viewerRay'

/** How long the player must be seated and stationary before the offer, seconds (06 §6). */
export const OFFER_AFTER_SECONDS = 30

/**
 * Head height below which a player is probably sitting, metres.
 *
 * 1.35. A seated adult's eyes are 1.1–1.3 m off the floor; a standing adult's are 1.5–1.8. The
 * threshold sits above the seated range so a tall person on a high stool still qualifies, and
 * below the standing range so a short person standing does not. `local-floor` makes this an
 * absolute measurement rather than a relative one, which is why the reference space matters.
 */
export const SEATED_HEIGHT = 1.35

/**
 * How far the head may wander and still be stationary, metres.
 *
 * 0.35 — a comfortable lean in any direction. A player in a chair leans forward to look at the
 * playfield and back to look at the backglass, and that is a 30 cm arc; a player on their feet
 * shifts their whole body around the machine and covers far more.
 */
export const STATIONARY_RADIUS = 0.35

/**
 * What the detector currently believes.
 *
 * **The dwell itself is not here, and it was** (M9-53, M9-57). `forSeconds` carried the seconds of
 * continuous seated evidence on both clients, and neither ever read it — the decision the detector
 * exists to make is `offer`, which already folds the 30-second dwell in, and 06 §6 asks for the
 * offer, not a stopwatch. An intermediate the state exposes is a claim somebody consumes it; this
 * one was measured on both sides of the language boundary and consumed on neither, so it went.
 */
export interface SeatedState {
  readonly seated: boolean
  /** True on the single frame the offer should be made. */
  readonly offer: boolean
}

export class SeatedDetector {
  private seconds = 0
  private anchorX = 0
  private anchorZ = 0
  private anchored = false
  private offered = false

  /** The player said no. Never ask again this session. */
  decline(): void {
    this.offered = true
  }

  /** The player said yes, or turned comfort on themselves. Same effect: stop asking. */
  accepted(): void {
    this.offered = true
  }

  /**
   * One frame of head pose, in the session's reference space.
   *
   * Returns the state, including a one-frame `offer` edge. The caller shows a prompt on that
   * edge and nothing on any other frame, which is what stops a prompt appearing every frame for
   * the rest of the session.
   */
  advance(headX: number, headY: number, headZ: number, frameSeconds: number): SeatedState {
    const low = headY > 0 && headY < SEATED_HEIGHT
    if (!low) {
      // Standing up resets the evidence completely. Half a minute of sitting followed by
      // standing up is a player who has stood up.
      this.seconds = 0
      this.anchored = false
      return { seated: false, offer: false }
    }

    if (!this.anchored) {
      this.anchorX = headX
      this.anchorZ = headZ
      this.anchored = true
    }

    const drift = Math.hypot(headX - this.anchorX, headZ - this.anchorZ)
    if (drift > STATIONARY_RADIUS) {
      // Moved somewhere else and sat down again, or walked while crouching. Re-anchor and start
      // the clock over rather than giving up: the player *is* low, they have simply moved.
      this.anchorX = headX
      this.anchorZ = headZ
      this.seconds = 0
      return { seated: false, offer: false }
    }

    this.seconds += frameSeconds
    const ready = this.seconds >= OFFER_AFTER_SECONDS
    const offer = ready && !this.offered
    if (offer) this.offered = true
    return { seated: ready, offer }
  }
}

/**
 * Where a player's hands rest, as a fraction of their own eye height.
 *
 * **A lockdown bar is at hip height, and hip height is where the hands are.** That is not a
 * coincidence and it is the whole ergonomic argument for the machine: you stand at it and your
 * hands fall onto the buttons. A standing adult's eyes are 1.65 m up and their hips — and a real
 * lockdown bar — are at 0.914, so the fraction is 0.554.
 *
 * **It holds sitting down**, which is what makes it the right rule rather than a fitted one: a
 * seated adult's eyes are around 1.20 m and their hands rest in their lap at about 0.66, which is
 * 0.554 again. So one number places the machine correctly for either posture without having to
 * decide which it is looking at.
 *
 * **This replaces a number that claimed to be this relationship and was not.** The first version
 * dropped the machine so its lockdown bar sat 0.18 m below the eyeline and said in its own comment
 * that this was "the same relationship a standing player has". It is not: 1.65 − 0.914 is 0.736,
 * four times as far. At 0.18 the playfield's back edge — 0.13 m above the bar — finishes 5 cm
 * under the eyeline, and a Quest reported exactly that: "the pinball machine shows at user's eye
 * level". Deriving the height from the *sightline* instead — feet at `eye − 1.65` — is the other
 * near miss: it puts the bar a fixed 0.736 m below the eyeline whatever the posture, which for a
 * seated player is 0.46 m off the floor, reported as "the pinball is lower than where the hands
 * should be". Only the ratio is right for both postures.
 */
export const HANDS_FRACTION_OF_EYE_HEIGHT = 0.554

/** Where this player's hands rest, given their eye height. Metres. */
export function handHeightFor(eyeHeight: number): number {
  return eyeHeight * HANDS_FRACTION_OF_EYE_HEIGHT
}

/**
 * How far below a **seated** player's eyeline the lockdown bar goes, metres.
 *
 * **Resting hand height is the wrong rule for somebody sitting down, and the headset said so
 * twice.** The ratio above is right for a standing player because standing is the posture the
 * machine was designed around: the hands fall onto the bar and the eyes look down about thirty
 * degrees at the playfield. Sitting breaks the pair. A seated adult at a 1.25 m eyeline has resting
 * hands near 0.69 m, and a machine with its bar there was reported as too low — twice, from two
 * different rules that both aimed at the hands.
 *
 * The constraint that actually binds, in the player's own words: *a seated player cannot put their
 * hands far down and still work a plunger and flipper buttons.* Thighs are in the way, the chair
 * fixes the shoulder height, and the usable band is a shallow one just below the eyeline rather
 * than wherever the arms would hang.
 *
 * 0.30 m. Above the resting-hand answer at every seated eye height, so the hands come *up* to the
 * machine the way they do at a real one, and shallow enough that the playfield is inside a
 * comfortable downward gaze rather than in somebody's lap.
 *
 * **This is the Quest client's `SeatedDetector.SeatedBarBelowEyeline`, four days late.** It landed
 * there on 21 Aug in a commit that touched three Quest files and nothing else, so this stack kept
 * the rule the device had already rejected — 0.235 m lower for the same seated player. `anchors.ts`
 * has cited `SEATED_BAR_BELOW_EYELINE` as though it lived here since 25 Aug, which is how long the
 * two clients have disagreed about where a seated player's machine goes.
 */
export const SEATED_BAR_BELOW_EYELINE = 0.30

/**
 * The range of head heights a floor-referenced runtime can be believed about.
 *
 * **A tracking origin that says it is floor-relative has agreed to a convention, not measured a
 * floor.** A Quest whose space setup was done at a desk reported eye heights of 3.44 m and −2.63 m
 * in one session, and every one of those was returned by a runtime that had cheerfully confirmed
 * a floor-relative reference space. WebXR's `local-floor` is the same promise from the same
 * runtimes, so the same guard applies here.
 */
export const LOWEST_BELIEVABLE_EYE = 1.0
export const HIGHEST_BELIEVABLE_EYE = 2.1

/** Whether a reported eye height is one a person could actually have. */
export function believableFloor(eyeHeight: number): boolean {
  return eyeHeight >= LOWEST_BELIEVABLE_EYE && eyeHeight <= HIGHEST_BELIEVABLE_EYE
}

/**
 * The eye height assumed when the runtime's own is not believable, metres.
 *
 * Seated rather than standing, because a floor wrong enough to be refused is almost always a
 * headset that was set up at a desk, and a player at a desk is sitting at it. Used only for the
 * *distance* between eyes and hands — never as a position, which is exactly the number that
 * cannot be trusted when this is reached for.
 */
export const NOMINAL_SEATED_EYE = 1.20

/**
 * Where the machine should stand for a seated player at `headY`.
 *
 * The cabinet is *lowered* so that its lockdown bar sits a little below the player's eyeline,
 * which is the geometry a standing player gets for free and a seated one does not. The floor is
 * not where the machine goes any more — the machine hangs, which is impossible for a real
 * cabinet and completely natural in a headset, and pretending otherwise would mean asking a
 * seated player to look down at their own knees for the whole game.
 *
 * `lockdownBarHeight` and `cabinetLength` come from the machine so a machine with a different
 * body gets a different offset, for the same reason `cabinetControls.ts` reads its dimensions
 * rather than typing them.
 *
 * **The distance was `SEATED_DISTANCE * scale`, and both halves of that were wrong.**
 * `SEATED_DISTANCE` was 0.85 — `ARM_REACH`, which `reachFor` documents as the furthest a
 * cabinet's **near edge** may stand — and it was used as the distance to the cabinet's **centre**.
 * A 1.295 m machine placed that way puts its lockdown bar 0.2025 m from the player's eyes, drawn
 * around them rather than in front of them. Scaling the whole distance by the cabinet's scale then
 * compounded it, because a player's body does not shrink when the machine does: at the tabletop
 * 0.42 the gap fell to **0.085 m**.
 *
 * `standOffFor` is the product's own derivation of the same sentence — `halfDepth * scale +
 * PLAYER_GAP`, where `PLAYER_GAP` is "where you stand at a pinball machine, close enough that the
 * lockdown bar is under your hands and far enough that the backbox is not against your face". That
 * is the seated requirement as much as the standing one: 06 §6 asks for "everything reachable
 * without standing or leaning", and the bar under the hands is exactly that. Only the *scaling*
 * part of the old rule differed by posture, and it differed by being wrong.
 */
export function seatedPlacement(
  current: Placement,
  headX: number,
  headY: number,
  headZ: number,
  headYaw: number,
  lockdownBarHeight: number,
  cabinetLength: number,
  knownEye: number = NOMINAL_SEATED_EYE,
  /**
   * The player's own adjustment to the drop, in metres (G2-02, 06 §6's "adjustable machine height
   * independent of scale"). Positive lowers the machine.
   *
   * **Passed in rather than read**, because this module has no storage and must not acquire one:
   * every number here is geometry, and a placement function that consulted `localStorage` could not
   * be driven through a range of postures by a test. `comfortPrefs.heightDropOffset` is the value,
   * `XRRuntime.reposition` is where the two meet.
   *
   * **Applied to the drop and never to the position, and never scaled.** A drop survives a tracking
   * space being renumbered and a world height does not — 06 §3.1: "the whole reason this placement
   * is head-referenced is that the floor could not be trusted" — and a player's body does not shrink
   * when the cabinet does, so multiplying this by `scale` would move the machine twice for one
   * decision and would shrink the correction exactly where a tabletop machine needs it most.
   */
  dropOffset = 0,
  /** An explicit seated choice takes precedence over an unreliable or standing-height origin. */
  forceSeated = false,
): Placement {
  const distance = standOffFor(current.scale, cabinetLength)
  // Directly in front of where the player is facing. **A head's yaw and a cabinet's yaw are two
  // different conventions and this is the one place both appear**: a head at yaw θ looks along
  // `(sin θ, −cos θ)` because 0 looks down −Z, while a cabinet at yaw θ *faces* `(−sin θ, cos θ)`
  // because its front is its local +Z. Mixing them puts the machine behind the player, and the
  // bug reads as "comfort mode does nothing".
  const x = headX + Math.sin(headYaw) * distance
  const z = headZ - Math.cos(headYaw) * distance
  // The machine's feet placed so that *its* bar lands where *this* player's hands are.
  // `lockdownBarHeight` is the machine's own, so a cabinet with a different body still ends up
  // with its own bar under the player's hands rather than somebody else's.
  //
  // **A head height that cannot be true is used as a position but never as a measurement.** When
  // the floor is wrong the eyeline is still where the player is looking from, so the machine has
  // to hang below *that* — but by the eyes-to-hands distance of a plausible person rather than of
  // whatever the runtime claimed, or a floor 2 m out drops the cabinet 2 m with it.
  // **A head height that cannot be true is used as a position but never as a measurement**, and
  // the person it is measured against is this player where one has been observed. A remembered
  // height beats the nominal one for the same reason `eyeHeightMemory.ts` exists: the floor's
  // error moves between sessions and the player's height does not.
  const measured = believableFloor(headY)
    ? headY
    : believableFloor(knownEye) ? knownEye : NOMINAL_SEATED_EYE
  // **Which posture, and therefore which rule.** The hip ratio is right for a standing player and
  // was reported as too low twice for a seated one — see `SEATED_BAR_BELOW_EYELINE`. The Quest
  // client has branched on this since 21 Aug and this stack did not, so the two put a seated
  // player's machine 0.235 m apart.
  //
  // Judged on `measured` rather than on `headY`, which is the one place this deliberately differs
  // from `MachinePreview.StandInFrontOfTheViewer`: that reads the raw eyeline and therefore treats
  // *any* unbelievable floor as seated. They agree wherever the floor is believable, because
  // `measured` is then `headY`. Where it is not, this asks the height the player is known to have
  // instead of assuming — which is the same preference `MixedRealityRig.Judge` states for judging a
  // restored anchor: a settled median beats a single reading, and both beat a guess.
  const standing = measured - handHeightFor(measured)
  const drop =
    (forceSeated || measured < SEATED_HEIGHT
      ? SEATED_BAR_BELOW_EYELINE
      : // **A cabinet may not be buried in a floor the player can see** — the cap the Quest client
        // has had in `DefaultDrop` since M7-37 and this one did not.
        //
        // The hands rule scales the whole body, and below about a 1.65 m eyeline it asks for a bar
        // lower than a real machine's — the cabinet being a fixed 0.914 m from bar to foot. Probed
        // against the real constants in the same change that found it: at a 1.35 m eyeline the base
        // landed **0.166 m underground**, and at 1.50 m, 0.083 m under.
        //
        // Nothing is invented: the cap *is* what standing the machine on the floor asks for, so a
        // player short enough to want a lower bar gets a real machine instead of a sunk one, and
        // everybody taller gets the ergonomic answer unchanged.
        //
        // **Only where the floor is believable.** The case this whole branch exists for is a floor
        // that is quietly wrong, and a cap measured against one would drag that error back in —
        // which is why it is applied to `measured` rather than to `headY`, and why an unbelievable
        // eye height reaches the hands answer with no cap at all.
        believableFloor(headY)
        ? Math.min(standing, measured - lockdownBarHeight * current.scale)
        : standing) + dropOffset
  // **The cap above is on the derivation and `dropOffset` is outside it, deliberately.** A control
  // that silently refused half its range is a row a short player presses twice and watches do
  // nothing — and a hanging cabinet is not a fault here: this function's own header says the machine
  // hangs, "which is impossible for a real cabinet and completely natural in a headset", and the
  // seated default already puts the feet a centimetre under at a 1.20 m eyeline. What is capped is
  // the answer nobody chose.
  const y = headY - drop - lockdownBarHeight * current.scale
  return { x, y, z, yaw: yawFacing(x, z, headX, headZ, current.yaw), scale: current.scale }
}
